import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Stepper from '../../components/Onboarding/Stepper';
import Step1Welcome from './Step1Welcome';
import Step2SPAPICredentials from './Step2SPAPICredentials';
import Step3AdsAPICredentials from './Step3AdsAPICredentials';
import Step4FirstSync from './Step4FirstSync';
import Step5Tour from './Step5Tour';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, completeOnboarding } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({
    spApiConfigured: false,
    adsApiConfigured: false,
    firstSyncCompleted: false,
    tourCompleted: false
  });

  const steps = [
    { number: 1, title: 'Welcome', description: 'Get started with Genia+' },
    { number: 2, title: 'SP-API', description: 'Connect your Amazon account' },
    { number: 3, title: 'Ads API', description: 'Connect Amazon Ads (optional)' },
    { number: 4, title: 'Sync', description: 'Import your data' },
    { number: 5, title: 'Tour', description: 'Explore features' }
  ];

  // Load saved progress
  useEffect(() => {
    const savedProgress = localStorage.getItem('onboarding_progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setCurrentStep(progress.currentStep || 1);
      setOnboardingData(progress.data || onboardingData);
    }
  }, []);

  // Save progress
  useEffect(() => {
    localStorage.setItem('onboarding_progress', JSON.stringify({
      currentStep,
      data: onboardingData
    }));
  }, [currentStep, onboardingData]);

  const handleNext = async (data = {}) => {
    setOnboardingData(prev => ({ ...prev, ...data }));
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      // Onboarding complete
      await completeOnboarding();
      localStorage.removeItem('onboarding_progress');
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep === 3) {
      // Skip Ads API
      handleNext({ adsApiConfigured: false });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Welcome user={user} onNext={handleNext} />;
      case 2:
        return (
          <Step2SPAPICredentials
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <Step3AdsAPICredentials
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkip}
          />
        );
      case 4:
        return (
          <Step4FirstSync
            onNext={handleNext}
            onBack={handleBack}
            spApiConfigured={onboardingData.spApiConfigured}
          />
        );
      case 5:
        return (
          <Step5Tour
            onNext={handleNext}
            onBack={handleBack}
            plan={user?.subscription?.plan}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome to Genia+ SaaS
          </h1>
          <p className="text-lg text-gray-600">
            Let's get you set up in just a few steps
          </p>
        </div>

        {/* Stepper */}
        <div className="mb-12">
          <Stepper steps={steps} currentStep={currentStep} />
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
